// Layouts/Assignments/AssignmentDetails.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import AddAssignmentModal from './parts/AddAssignmentModal';
import GradableSubmissionCard, { Submission } from './parts/GradableSubmissionCard';
import BulkGradeModal from './parts/BulkGradeModal';

// ── Design tokens (navy / gold — matches Batches.tsx / BatchDetailsPage.tsx) ──
const C = {
  navy:       '#0e225d',
  navyMid:    '#1a3370',
  navyDark:   '#0a1a3d',
  gold:       '#fbb21f',
  goldLight:  '#ffc93c',
  goldDim:    'rgba(251,178,31,0.18)',
  goldBorder: 'rgba(251,178,31,0.35)',
  white:      '#ffffff',
  border:     '#E5E7EB',
  bg:         '#F5F7FA',
  textMuted:  '#9ca3af',
  textSub:    '#6b7280',
  textBody:   '#1f2937',
};

interface AssignmentFile {
  file_name: string;
  url: string;
  size: number;
}

interface Assignment {
  assignment_id: string;
  institute_id: string;
  branch_id: string | null;
  branch_name: string | null;
  batch_id: string;
  batch_name: string | null;
  assignment_title: string;
  assignment_body: string;
  notes: string;
  files: AssignmentFile[];
  start_date: string | null;
  start_time: string | null;
  submission_date: string | null;
  submission_time: string | null;
  allow_late_submissions: boolean;
  created_at: string;
  created_by: string;
  created_by_name: string | null;
}

interface GradeData {
  marks_obtained: string;
  total_marks: string;
  feedback: string;
}

interface SubmissionToGrade {
  submission_id: string;
  marks_obtained: number;
  total_marks: number;
  feedback: string;
}

const AssignmentDetailsPage: React.FC = () => {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);

  const [assignment, setAssignment] = useState<Assignment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [submissionsTotal, setSubmissionsTotal] = useState(0);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);

  const [selectedSubmissions, setSelectedSubmissions] = useState<Set<string>>(new Set());
  const [bulkGradeData, setBulkGradeData] = useState<{ [key: string]: GradeData }>({});
  const [showBulkGradeModal, setShowBulkGradeModal] = useState(false);
  const [isGrading, setIsGrading] = useState(false);

  useEffect(() => {
    if (assignmentId) {
      fetchAssignment();
      fetchSubmissions();
    }
  }, [assignmentId]);

  const fetchAssignment = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiClient.get(`/v1/assignments/${assignmentId}`);
      if (res.data.success) {
        setAssignment(res.data.data);
      } else {
        setError('Failed to load assignment details');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load assignment details');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubmissions = async () => {
    if (!assignmentId) return;
    try {
      setSubmissionsLoading(true);
      const res = await apiClient.get(`/v1/assignment-submissions/by-assignment/${assignmentId}`, {
        params: { limit: 100 },
      });
      if (res.data.success) {
        setSubmissions(res.data.data);
        setSubmissionsTotal(res.data.pagination.total);
      }
    } catch (err) {
      console.error('Failed to fetch submissions:', err);
    } finally {
      setSubmissionsLoading(false);
    }
  };

  const ungradedSubmissions = submissions.filter(s => s.status !== 'graded');
  const gradedCount = submissions.length - ungradedSubmissions.length;

  const toggleSubmissionSelection = (submissionId: string) => {
    const newSelected = new Set(selectedSubmissions);
    if (newSelected.has(submissionId)) {
      newSelected.delete(submissionId);
      const newGradeData = { ...bulkGradeData };
      delete newGradeData[submissionId];
      setBulkGradeData(newGradeData);
    } else {
      newSelected.add(submissionId);
      setBulkGradeData({
        ...bulkGradeData,
        [submissionId]: { marks_obtained: '', total_marks: '', feedback: '' },
      });
    }
    setSelectedSubmissions(newSelected);
  };

  const selectAllSubmissions = () => {
    if (selectedSubmissions.size === ungradedSubmissions.length) {
      setSelectedSubmissions(new Set());
      setBulkGradeData({});
    } else {
      const newSelected = new Set(ungradedSubmissions.map(s => s.submission_id));
      const newGradeData: { [key: string]: GradeData } = {};
      ungradedSubmissions.forEach(s => {
        newGradeData[s.submission_id] = { marks_obtained: '', total_marks: '', feedback: '' };
      });
      setSelectedSubmissions(newSelected);
      setBulkGradeData(newGradeData);
    }
  };

  const updateBulkGradeField = (submissionId: string, field: keyof GradeData, value: string) => {
    setBulkGradeData({
      ...bulkGradeData,
      [submissionId]: { ...bulkGradeData[submissionId], [field]: value },
    });
  };

  const handleBulkGrade = async () => {
    const submissionsToGrade: SubmissionToGrade[] = [];

    for (const submissionId of Array.from(selectedSubmissions)) {
      const data = bulkGradeData[submissionId];

      if (!data.marks_obtained || !data.total_marks) {
        toast.current?.show({ severity: 'warn', summary: 'Validation Error', detail: 'Please fill in marks for all selected submissions', life: 4000 });
        return;
      }

      const marksObtained = parseInt(data.marks_obtained, 10);
      const totalMarks = parseInt(data.total_marks, 10);

      if (isNaN(marksObtained) || isNaN(totalMarks)) {
        toast.current?.show({ severity: 'warn', summary: 'Validation Error', detail: 'Marks must be valid numbers', life: 4000 });
        return;
      }
      if (marksObtained < 0 || totalMarks <= 0) {
        toast.current?.show({ severity: 'warn', summary: 'Validation Error', detail: 'Invalid marks values', life: 4000 });
        return;
      }
      if (marksObtained > totalMarks) {
        toast.current?.show({ severity: 'warn', summary: 'Validation Error', detail: 'Marks obtained cannot exceed total marks', life: 4000 });
        return;
      }

      submissionsToGrade.push({
        submission_id: submissionId,
        marks_obtained: marksObtained,
        total_marks: totalMarks,
        feedback: data.feedback || '',
      });
    }

    if (!window.confirm(`Are you sure you want to grade ${submissionsToGrade.length} submission(s)?`)) return;

    setIsGrading(true);
    try {
      const response = await apiClient.put('/v1/assignment-submissions/bulk-grade', {
        submissions: submissionsToGrade,
      });

      if (response.data.success) {
        const { success_count, error_count, errors } = response.data.data;
        toast.current?.show({
          severity: error_count > 0 ? 'warn' : 'success',
          summary: error_count > 0 ? 'Partially graded' : 'Success',
          detail: error_count > 0
            ? `Graded ${success_count} submission(s). ${error_count} failed — check console.`
            : `Graded ${success_count} submission(s) successfully!`,
          life: 4000,
        });
        if (error_count > 0) console.error('Grading errors:', errors);

        setSelectedSubmissions(new Set());
        setBulkGradeData({});
        setShowBulkGradeModal(false);
        await fetchSubmissions();
      }
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.error || 'Failed to grade submissions', life: 4000 });
    } finally {
      setIsGrading(false);
    }
  };

  const handleEditSuccess = () => {
    toast.current?.show({ severity: 'success', summary: 'Updated', detail: 'Assignment updated successfully!', life: 3000 });
    setShowEditModal(false);
    fetchAssignment();
  };

  const formatDate = (d: string | null) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return d; }
  };

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', background: C.white, borderRadius: '16px', border: `1px solid ${C.border}` }}>
        <div style={{ textAlign: 'center' }}>
          <ProgressSpinner style={{ width: '48px', height: '48px' }} strokeWidth="3" animationDuration=".8s" />
          <p style={{ marginTop: '16px', color: C.navy, fontSize: '0.95rem', fontWeight: '500' }}>Loading assignment details...</p>
        </div>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────
  if (error || !assignment) {
    return (
      <div style={{ padding: '24px' }}>
        <Message severity="error" text={error || 'Assignment not found'} style={{ marginBottom: '20px', fontSize: '0.9rem' }} />
        <button
          onClick={() => navigate('/assignments')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 20px', background: C.navy, border: 'none', borderRadius: '10px', color: C.white, fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer' }}
        >
          <i className="pi pi-arrow-left" /> Back to Assignments
        </button>
      </div>
    );
  }

  // ── Main ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '0', margin: '0 auto', minHeight: '100vh' }}>
      <Toast ref={toast} />

      {/* ── Hero Header — navy gradient (mirrors BatchDetailsPage.tsx hero) ── */}
      <div style={{
        background: `linear-gradient(180deg, ${C.navy} 0%, ${C.navyMid} 50%, ${C.navyDark} 100%)`,
        borderRadius: '20px', padding: '32px', marginBottom: '24px',
        color: C.white, position: 'relative', overflow: 'hidden',
        boxShadow: '2px 0 12px rgba(14,34,93,0.3)',
      }}>
        <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '220px', height: '220px', background: C.goldDim, borderRadius: '50%', filter: 'blur(50px)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-40px', left: '30%', width: '140px', height: '140px', background: 'rgba(251,178,31,0.05)', borderRadius: '50%', filter: 'blur(40px)', pointerEvents: 'none' }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          <button
            onClick={() => navigate('/assignments')}
            style={{
              background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
              color: C.white, padding: '8px 16px', borderRadius: '8px', cursor: 'pointer',
              fontSize: '0.8rem', fontWeight: '600', display: 'inline-flex', alignItems: 'center',
              gap: '8px', marginBottom: '20px', transition: 'all 0.2s ease',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
          >
            <i className="pi pi-arrow-left" /> Back to Assignments
          </button>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '24px' }}>
            <div>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                padding: '4px 12px', background: C.goldDim, border: `1px solid ${C.goldBorder}`,
                borderRadius: '20px', marginBottom: '12px',
                fontSize: '0.68rem', fontWeight: '700', letterSpacing: '0.8px',
                textTransform: 'uppercase', color: C.gold,
              }}>
                <i className="pi pi-file-edit" style={{ fontSize: '0.7rem' }} />
                Assignment Details
              </div>

              <h1 style={{ margin: '0 0 6px 0', fontSize: '1.75rem', fontWeight: '700', color: C.white, letterSpacing: '-0.5px' }}>
                {assignment.assignment_title}
              </h1>
              <p style={{ margin: '0 0 16px 0', color: 'rgba(255,255,255,0.6)', fontSize: '0.88rem' }}>
                {assignment.batch_name || 'No Batch'} {assignment.branch_name ? `· ${assignment.branch_name}` : ''}
              </p>

              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {[
                  { icon: 'pi-calendar', label: `Submit by ${formatDate(assignment.submission_date)}` },
                  { icon: 'pi-paperclip', label: `${assignment.files.length} File${assignment.files.length !== 1 ? 's' : ''}` },
                  { icon: 'pi-users', label: `${submissionsTotal} Submission${submissionsTotal !== 1 ? 's' : ''}` },
                  { icon: 'pi-check-circle', label: `${gradedCount} Graded` },
                ].map(chip => (
                  <div key={chip.label} style={{
                    background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)',
                    padding: '8px 14px', borderRadius: '10px',
                    border: '1px solid rgba(255,255,255,0.15)',
                    display: 'flex', alignItems: 'center', gap: '8px',
                  }}>
                    <i className={`pi ${chip.icon}`} style={{ fontSize: '0.8rem', color: C.gold }} />
                    <span style={{ fontSize: '0.82rem', fontWeight: '600', color: C.white }}>{chip.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px' }}>
              {assignment.allow_late_submissions && (
                <span style={{
                  padding: '4px 12px', borderRadius: '20px',
                  fontSize: '0.68rem', fontWeight: '700', letterSpacing: '0.5px',
                  background: C.gold, color: C.navy,
                }}>
                  LATE SUBMISSIONS ALLOWED
                </span>
              )}

              <button
                onClick={() => setShowEditModal(true)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '8px',
                  padding: '12px 24px', background: C.gold,
                  border: 'none', borderRadius: '12px', color: C.navy,
                  fontSize: '0.88rem', fontWeight: '700', cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(251,178,31,0.4)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', letterSpacing: '0.3px',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(251,178,31,0.5)';
                  e.currentTarget.style.background = C.goldLight;
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(251,178,31,0.4)';
                  e.currentTarget.style.background = C.gold;
                }}
              >
                <i className="pi pi-pencil" style={{ fontSize: '0.9rem' }} />
                EDIT ASSIGNMENT
              </button>

              <p style={{ margin: 0, fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)' }}>
                Created {formatDate(assignment.created_at)} {assignment.created_by_name ? `by ${assignment.created_by_name}` : ''}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Body Grid ────────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '20px', marginBottom: '24px' }}>

        {/* ── Left column ──────────────────────────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* Description */}
          {assignment.assignment_body && (
            <SectionCard title="Description" icon="pi-align-left">
              <p style={{ margin: 0, fontSize: '0.88rem', color: C.textBody, lineHeight: '1.6' }}>
                {assignment.assignment_body}
              </p>
            </SectionCard>
          )}

          {/* Notes */}
          {assignment.notes && (
            <SectionCard title="Notes" icon="pi-info-circle">
              <div style={{ display: 'flex', gap: '10px', background: '#FFF5E6', border: `1.5px solid ${C.gold}`, borderRadius: '12px', padding: '14px' }}>
                <i className="pi pi-info-circle" style={{ fontSize: '1rem', color: C.gold, flexShrink: 0, marginTop: '2px' }} />
                <p style={{ margin: 0, fontSize: '0.85rem', color: C.navy, lineHeight: '1.5' }}>{assignment.notes}</p>
              </div>
            </SectionCard>
          )}

          {/* Files */}
          <SectionCard title={`Attached Files (${assignment.files.length})`} icon="pi-paperclip">
            {assignment.files.length === 0 ? (
              <EmptyState icon="pi-paperclip" message="No files attached to this assignment." />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {assignment.files.map((file, idx) => (
                  <a
                    key={idx}
                    href={file.url}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      display: 'flex', alignItems: 'center', gap: '12px',
                      background: C.bg, padding: '12px 14px', borderRadius: '10px',
                      border: `1px solid ${C.border}`, textDecoration: 'none',
                    }}
                  >
                    <div style={{ width: '36px', height: '36px', borderRadius: '9px', background: 'rgba(14,34,93,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <i className="pi pi-file" style={{ fontSize: '0.9rem', color: C.navy }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: 600, color: C.navy, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {file.file_name}
                      </p>
                      <p style={{ margin: 0, fontSize: '0.74rem', color: C.textMuted }}>
                        {(file.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                    <i className="pi pi-download" style={{ fontSize: '0.85rem', color: C.gold }} />
                  </a>
                ))}
              </div>
            )}
          </SectionCard>

          {/* Submissions + grading */}
          <SectionCard title={`Submissions (${submissionsTotal})`} icon="pi-users">
            {submissionsLoading && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '30px' }}>
                <ProgressSpinner style={{ width: '32px', height: '32px' }} strokeWidth="3" animationDuration=".8s" />
              </div>
            )}

            {!submissionsLoading && submissions.length === 0 && (
              <EmptyState icon="pi-inbox" message="No submissions yet." />
            )}

            {!submissionsLoading && submissions.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {/* Bulk actions bar */}
                {ungradedSubmissions.length > 0 && (
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    background: C.bg, padding: '12px 14px', borderRadius: '10px', border: `1px solid ${C.border}`,
                    flexWrap: 'wrap', gap: '10px',
                  }}>
                    <button
                      onClick={selectAllSubmissions}
                      style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'transparent', border: 'none', cursor: 'pointer' }}
                    >
                      <i className={`pi ${selectedSubmissions.size === ungradedSubmissions.length ? 'pi-check-square' : 'pi-stop'}`} style={{ fontSize: '1rem', color: C.navy }} />
                      <span style={{ fontSize: '0.82rem', fontWeight: 600, color: C.navy }}>
                        {selectedSubmissions.size === ungradedSubmissions.length ? 'Deselect All' : 'Select All'}
                      </span>
                    </button>

                    {selectedSubmissions.size > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '0.78rem', fontWeight: 700, color: C.textSub }}>
                          {selectedSubmissions.size} selected
                        </span>
                        <button
                          onClick={() => setShowBulkGradeModal(true)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '7px',
                            background: C.gold, border: 'none', borderRadius: '9px',
                            padding: '8px 16px', color: C.navy, fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer',
                          }}
                        >
                          <i className="pi pi-star-fill" style={{ fontSize: '0.78rem' }} />
                          Grade
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Submission cards */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {submissions.map((submission) => (
                    <GradableSubmissionCard
                      key={submission.submission_id}
                      submission={submission}
                      isSelected={selectedSubmissions.has(submission.submission_id)}
                      onToggleSelect={toggleSubmissionSelection}
                    />
                  ))}
                </div>
              </div>
            )}
          </SectionCard>
        </div>

        {/* ── Right column ─────────────────────────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* At a Glance */}
          <div style={{ background: C.white, borderRadius: '16px', padding: '20px', boxShadow: '0 2px 8px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{ width: '36px', height: '36px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="pi pi-chart-bar" style={{ color: C.gold, fontSize: '1rem' }} />
              </div>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: C.navy }}>At a Glance</h3>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[
                { icon: 'pi-users', label: 'Total Submissions', value: submissionsTotal },
                { icon: 'pi-check-circle', label: 'Graded', value: gradedCount },
                { icon: 'pi-clock', label: 'Pending Review', value: ungradedSubmissions.length },
              ].map(stat => (
                <div key={stat.label} style={{ display: 'flex', alignItems: 'center', gap: '14px', background: C.bg, padding: '14px', borderRadius: '12px', border: `1px solid ${C.border}` }}>
                  <div style={{ width: '44px', height: '44px', flexShrink: 0, background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className={`pi ${stat.icon}`} style={{ color: C.gold, fontSize: '1rem' }} />
                  </div>
                  <div>
                    <p style={{ margin: 0, fontSize: '0.7rem', color: C.textMuted, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{stat.label}</p>
                    <p style={{ margin: 0, fontSize: '1.6rem', fontWeight: '800', color: C.navy, lineHeight: 1.1 }}>{stat.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Dates */}
          <div style={{ background: C.white, borderRadius: '16px', padding: '20px', boxShadow: '0 2px 8px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{ width: '36px', height: '36px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="pi pi-calendar" style={{ color: C.gold, fontSize: '1rem' }} />
              </div>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: C.navy }}>Dates</h3>
            </div>
            <div style={{ background: C.bg, borderRadius: '10px', padding: '14px', border: `1px solid ${C.border}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                <div>
                  <p style={{ margin: '0 0 3px 0', fontSize: '0.68rem', color: C.textMuted, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Start</p>
                  <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: '700', color: C.navy }}>
                    {formatDate(assignment.start_date)} {assignment.start_time ? `· ${assignment.start_time}` : ''}
                  </p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p style={{ margin: '0 0 3px 0', fontSize: '0.68rem', color: C.textMuted, fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Submission</p>
                  <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: '700', color: C.navy }}>
                    {formatDate(assignment.submission_date)} {assignment.submission_time ? `· ${assignment.submission_time}` : ''}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div style={{ background: C.white, borderRadius: '16px', padding: '20px', boxShadow: '0 2px 8px rgba(14,34,93,0.06)', border: `1px solid ${C.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
              <div style={{ width: '36px', height: '36px', background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="pi pi-bolt" style={{ color: C.gold, fontSize: '1rem' }} />
              </div>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: C.navy }}>Quick Actions</h3>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[
                { icon: 'pi-pencil', label: 'Edit Assignment', onClick: () => setShowEditModal(true) },
                { icon: 'pi-list', label: 'Back to Assignments', onClick: () => navigate('/assignments') },
              ].map(action => (
                <button
                  key={action.label}
                  onClick={action.onClick}
                  style={{
                    width: '100%', padding: '11px 14px',
                    background: C.bg, border: `1px solid ${C.border}`,
                    borderRadius: '10px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '10px',
                    transition: 'all 0.2s ease', textAlign: 'left',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = C.navy;
                    e.currentTarget.style.borderColor = C.navy;
                    (e.currentTarget.querySelector('.qa-icon') as HTMLElement).style.color = C.gold;
                    (e.currentTarget.querySelector('.qa-label') as HTMLElement).style.color = C.white;
                    (e.currentTarget.querySelector('.qa-chevron') as HTMLElement).style.color = 'rgba(255,255,255,0.4)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = C.bg;
                    e.currentTarget.style.borderColor = C.border;
                    (e.currentTarget.querySelector('.qa-icon') as HTMLElement).style.color = C.navy;
                    (e.currentTarget.querySelector('.qa-label') as HTMLElement).style.color = C.textBody;
                    (e.currentTarget.querySelector('.qa-chevron') as HTMLElement).style.color = C.textMuted;
                  }}
                >
                  <i className={`pi ${action.icon} qa-icon`} style={{ fontSize: '0.9rem', color: C.navy, transition: 'color 0.2s' }} />
                  <span className="qa-label" style={{ fontSize: '0.85rem', fontWeight: '600', color: C.textBody, transition: 'color 0.2s' }}>{action.label}</span>
                  <i className="pi pi-chevron-right qa-chevron" style={{ marginLeft: 'auto', fontSize: '0.7rem', color: C.textMuted, transition: 'color 0.2s' }} />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Edit Modal ───────────────────────────────────────────────────── */}
      <AddAssignmentModal
        visible={showEditModal}
        instituteId={assignment.institute_id}
        branchId={assignment.branch_id || ''}
        editingAssignment={assignment}
        onHide={() => setShowEditModal(false)}
        onSuccess={handleEditSuccess}
      />

      {/* ── Bulk Grade Modal ─────────────────────────────────────────────── */}
      <BulkGradeModal
        visible={showBulkGradeModal}
        onClose={() => setShowBulkGradeModal(false)}
        selectedSubmissions={selectedSubmissions}
        submissions={submissions}
        bulkGradeData={bulkGradeData}
        onUpdateGradeField={updateBulkGradeField}
        onSubmit={handleBulkGrade}
        isGrading={isGrading}
      />
    </div>
  );
};

// ── Sub-components (match BatchDetailsPage.tsx exactly) ────────────────────

const SectionCard: React.FC<{ title: string; icon: string; children: React.ReactNode }> = ({ title, icon, children }) => (
  <div style={{
    background: '#ffffff', borderRadius: '16px', padding: '24px',
    boxShadow: '0 2px 8px rgba(14,34,93,0.06)', border: '1px solid #E5E7EB',
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
      <div style={{
        width: '40px', height: '40px',
        background: 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)',
        borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <i className={`pi ${icon}`} style={{ color: '#fbb21f', fontSize: '1.1rem' }} />
      </div>
      <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '700', color: '#0e225d' }}>{title}</h2>
    </div>
    {children}
  </div>
);

const EmptyState: React.FC<{ icon: string; message: string }> = ({ icon, message }) => (
  <div style={{ padding: '40px 20px', textAlign: 'center', background: '#F5F7FA', borderRadius: '12px', border: '2px dashed #E5E7EB' }}>
    <div style={{ width: '52px', height: '52px', background: 'rgba(14,34,93,0.06)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
      <i className={`pi ${icon}`} style={{ fontSize: '1.5rem', color: '#0e225d', opacity: 0.25 }} />
    </div>
    <p style={{ margin: 0, color: '#9ca3af', fontSize: '0.9rem' }}>{message}</p>
  </div>
);

export default AssignmentDetailsPage;
